import React, { ReactElement, useState } from "react";
import { Box, Button, Flex, Heading } from "@govlaunch/web";
import { IStoryFieldsFragment } from "../fragments/__generated__/StoryFieldsFragment.generated";
import { constructCoauthors, StoryAuthorOneLineCard, TCoauthor } from "./StoryAuthor";
import { ArrowDownIcon, ArrowUpIcon } from "./ArrowIcons";
import { FormattedMessage, useIntl } from "react-intl";

interface IStoryCoauthorsProps {
  coauthors: TCoauthor[];
  renderLinkWrap?: (
    coauthor: NonNullable<IStoryFieldsFragment["coauthors"]>[0],
    children: ReactElement,
  ) => ReactElement;
  renderLinkWrapVendor?: (
    vendor: NonNullable<IStoryFieldsFragment["vendorCoauthors"]>[0],
    children: ReactElement,
  ) => ReactElement;
  isMobile?: boolean;
}

export default function StoryCoauthors({
  coauthors,
  renderLinkWrap,
  renderLinkWrapVendor,
  isMobile = false,
  ...props
}: IStoryCoauthorsProps) {
  const [showAll, setShowAll] = useState(false);
  const intl = useIntl();

  if (!coauthors || coauthors.length === 0) {
    return null;
  }

  const manyCoauthors = coauthors.length > 1;
  const showButton = manyCoauthors && isMobile;
  const sliceArray = !showAll && isMobile;

  return (
    <>
      <Heading as="h3" color="blue.500" fontWeight="bold" fontSize="18" mb={4}>
        <FormattedMessage defaultMessage="Coauthors" id="8l11qK" />
      </Heading>
      <div css={{ paddingLeft: 10 }}>
        <Flex direction="column" alignItems="flex-start" {...props}>
          {coauthors.slice(0, sliceArray ? 1 : coauthors.length).map((coauthor, index) => {
            const card = (
              <div css={{ marginBottom: 10 }}>
                <StoryAuthorOneLineCard
                  key={coauthor.slug}
                  author={coauthor}
                  cardTitle={intl.formatMessage({ defaultMessage: 'CO-AUTHOR', id: 'Qo82cW' })}
                  css={{ backgroundColor: "white" }}
                  showDivider={manyCoauthors && index !== coauthors.length - 1}
                  flexWrap={coauthor.__typename === "Company" ? "nowrap" : "wrap"}
                  fontSize={16}
                />
              </div>
            );

            if (coauthor.__typename === "Company") {
              return renderLinkWrapVendor
                ? renderLinkWrapVendor(coauthor as NonNullable<IStoryFieldsFragment["vendorCoauthors"]>[0], card)
                : card;
            }

            return renderLinkWrap
              ? renderLinkWrap(coauthor as NonNullable<IStoryFieldsFragment["coauthors"]>[0], card)
              : card;
          })}

          {showButton && (
            <Box py={3}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAll(!showAll)}
                aria-label={
                  showAll
                    ? intl.formatMessage({ defaultMessage: 'Show Less', id: 'PDd9N4' })
                    : intl.formatMessage({ defaultMessage: 'Show More', id: 'O8Z8t9' })
                }
                rightIcon={(props) => (showAll ? <ArrowUpIcon {...props} /> : <ArrowDownIcon {...props} />)}
              >
                {showAll ? (
                  <FormattedMessage defaultMessage="Show Less" id="PDd9N4" />
                ) : (
                  <FormattedMessage defaultMessage="Show More" id="O8Z8t9" />
                )}
              </Button>
            </Box>
          )}
        </Flex>
      </div>
    </>
  );
}
